import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { div } from 'framer-motion/client';

interface FAQItem {
  question: string;
  answer: string;
  category?: string;
}

interface StructuredFAQProps {
  faqs: FAQItem[];
  title?: string;
  className?: string;
}

const StructuredFAQ: React.FC<StructuredFAQProps> = ({
  faqs,
  className = ""
}) => {
  const [expandedIndex, setExpandedIndex] = React.useState<number | null>(null);

  // Generate structured data for FAQs
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className={className}>
      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <div className="max-w-4xl mx-auto relative">
        {/* Enhanced Title Section */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >


          <h2 className="hero-title text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight" style={{ color: '#368EC7' }}>
            Frequently Asked Questions
          </h2>

          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Everything you need to know about Quixocial and how it works
          </p>
        </motion.div>

        <div className="space-y-6">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              className="group relative"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -2 }}
            >
              {/* Subtle glow effect */}
              <div className="absolute -inset-0.5 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-500" style={{ background: 'linear-gradient(to right, rgba(48, 179, 221, 0.1), rgba(74, 41, 138, 0.1))' }}></div>

              <div className="relative bg-white/95 backdrop-blur-sm border border-gray-200/50 rounded-2xl shadow-lg group-hover:shadow-xl transition-all duration-500 overflow-hidden">
                {/* Animated background gradient */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: 'linear-gradient(to right, rgba(48, 179, 221, 0.05), rgba(74, 41, 138, 0.05))' }}></div>

                <button
                  className="relative w-full px-8 py-6 text-left flex justify-between items-center transition-all duration-300"
                  onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                  aria-expanded={expandedIndex === index}
                >
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 pr-6 group-hover:text-blue-700 transition-colors">
                    {faq.question}
                  </h3>

                  <motion.div
                    animate={{ rotate: expandedIndex === index ? 180 : 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="flex-shrink-0"
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow duration-300" style={{ background: 'linear-gradient(135deg, #30b3dd, #4a298a)' }}>
                      <ChevronDown className="w-5 h-5 text-white" />
                    </div>
                  </motion.div>
                </button>

                <AnimatePresence>
                  {expandedIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="px-8 pb-6">
                        <div className="pt-4 border-t border-gradient-to-r from-blue-200/50 to-purple-200/50">
                          <motion.p
                            initial={{ y: 10, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.3, delay: 0.1 }}
                            className="text-black leading-relaxed text-base relative z-10"
                          >
                            {faq.answer}
                          </motion.p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Bottom accent line */}
                <div className="absolute bottom-0 left-0 right-0 h-1 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)' }}></div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <p className="text-gray-600 mb-6">
            Still have questions? We're here to help!
          </p>
          <Link to="/contact">
            <motion.div
              className="inline-flex items-center px-8 py-4 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
              style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)' }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'linear-gradient(to right, #2a9cc7, #3f2479)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'linear-gradient(to right, #30b3dd, #4a298a)'}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              Contact Support
            </motion.div>
          </Link>
        </motion.div>

      </div>
    </div>
  );
};

export default StructuredFAQ;